import React, { useState, useEffect, useRef, useMemo } from "react";
import { Star, TrendingUp, Map, Calendar, CloudUpload } from "lucide-react";
import { useFetchProviderDispatchDataGoogleETAMutation } from "../../../features/appointment/appointmentApiSlice";
import moment from "moment";
import { Tooltip } from "@mui/material";
import classNames from "classnames";
import { pushFaroMeasurement } from "../../../services/faro";

const getScoreColor = (score) => {
  switch (true) {
    case score >= 90:
      return "bg-green-100 text-green-700";
    case score >= 75:
      return "bg-blue-100 text-blue-700";
    case score >= 60:
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-red-100 text-red-700";
  }
};

const getDistanceColor = (added) => {
  switch (true) {
    case added < 0:
      return "bg-green-100 text-green-700";
    case added <= 25:
      return "bg-yellow-100 text-yellow-700";
    case added <= 50:
      return "bg-orange-100 text-orange-700";
    default:
      return "bg-red-100 text-red-700";
  }
};

// Re-checks a row against the SAME bucket the Routing Criteria dropdown
// selects (<50/<75/<100/>=100 miles), but against the real, ETA-enriched
// added distance rather than /dispatch's pre-enrichment estimate - the
// backend no longer excludes by distance at all (see matchesRoutingCriteria
// in appointment.service.ts), so this is the only place that decision is
// actually made. Shared between TableRow (to hide a non-matching row) and
// TableComponent (to know how many rows are actually visible, so it can
// show a real empty state instead of an apparently-populated but visually
// empty table).
const rowMeetsRoutingCriteria = (provider, routingCriteria) => {
  const displayDistance = provider.proposedDistance - provider.originalDistance;
  return routingCriteria?.value != null
    ? displayDistance < routingCriteria.value
    : displayDistance >= 100; // "≥ 100 miles" option has value: null
};

// The backend reports, per search, why specific providers were excluded
// before distance was ever considered (e.g. insurance/language mismatch) -
// dispatchErrors from the /dispatch response. Surfaced next to any empty
// result state so "why am I seeing nothing" has an answer beyond guessing.
const DispatchErrorsList = ({ dispatchErrors }) => {
  if (!dispatchErrors?.length) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 items-center mt-3 text-sm text-gray-500">
      <p className="font-medium text-gray-600">Reasons reported by the search:</p>
      <ul className="list-disc list-inside">
        {dispatchErrors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    </div>
  );
};

const TableRow = ({
  rank,
  provider,
  providersData,
  fetchAppointmentsFunction,
  setProviderAppointment,
  setOpenProviderAppointments,
  view,
  setOpenMap,
  setOpenConfirmModal,
  setIsPublished,
  user,
  routingCriteria,
}) => {
  const providerDetails = providersData.find(
    (pro) => pro.id === provider.providerId
  );

  // providerDetails.name has a market label baked into it server-side (e.g.
  // "Aisha Wagner (FL)"), but it's one static string per provider - always
  // the provider's primary market when no market was selected in the search.
  // A single provider can have rows from multiple markets in the same result
  // set, so each row must show the market that actually produced *that*
  // slot (provider.marketName, from the possibleInsertions entry) rather
  // than the provider-level label.
  const displayProviderName = provider.marketName
    ? `${(providerDetails?.name ?? "").replace(/\s*\([^)]*\)\s*$/, "")} (${provider.marketName
      })`
    : providerDetails?.name;

  const handleClick = async () => {
    const providerAppointment = { ...provider, ...providerDetails };
    setProviderAppointment(providerAppointment);
    await fetchAppointmentsFunction(providerAppointment);
    setOpenProviderAppointments(true);
  };

  const handleMapClick = async () => {
    const providerAppointment = { ...provider, ...providerDetails };
    setProviderAppointment(providerAppointment);
    await fetchAppointmentsFunction(providerAppointment);
    setOpenMap(true);
  };
  const handlePublishClick = async () => {
    const providerAppointment = { ...provider, ...providerDetails };
    setProviderAppointment(providerAppointment);
    await fetchAppointmentsFunction(providerAppointment);
    setOpenConfirmModal(true);
    setIsPublished(true);
  };

  // const added = (provider.proposedDistance - provider.originalDistance).toFixed(
  //   2
  // );
  // Both the raw /dispatch estimate and the ETA-enriched value (once
  // fetchGoogleETA fills it in) are one-way for a first-of-day slot - no
  // round-trip halving needed here.
  const rawDistanceIncrease = provider.proposedDistance - provider.originalDistance;
  const displayDistance = rawDistanceIncrease;

  const rawDurationIncrease =
    provider.proposedDuration - provider.originalDuration;
  const displayDurationIncrease = rawDurationIncrease;

  // TableComponent already filters currentItems down to only rows that pass
  // rowMeetsRoutingCriteria before mapping them here, so this is a defensive
  // re-check rather than the primary gate.
  if (!rowMeetsRoutingCriteria(provider, routingCriteria)) {
    return null;
  }

  const added = displayDistance.toFixed(2);
  const addedRaw = rawDistanceIncrease.toFixed(2);
  const driveTime = (displayDurationIncrease / 60).toFixed(0);
  return (
    <tr key={provider.id} 
    // className="hover:bg-gray-50"
     className={`hover:bg-gray-50 ${provider.isLocked ? 'border-2 border-green-500 !border-green-500 shadow-sm ' : ''}`}
      style={{borderColor:provider?.isLocked ? '#21c45d !important' : ''}}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex justify-center">
          <div
            className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-medium
                  ${getScoreColor(provider.score)}
                `}
          >
            {rank}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-row gap-2 items-center">
          <div className="font-medium text-gray-900">
            {displayProviderName}{" "}
          </div>
          {(providerDetails?.employmentType === "Full-Time" ||
            providerDetails?.employmentType === "Per Diem") && (
              <div
                className={classNames(
                  "text-sm text-white font-bold px-2 py-[2px] rounded-md shadow-md",
                  providerDetails?.employmentType === "Full-Time"
                    ? "bg-primaryblue"
                    : "bg-primaryorange"
                )}
                title={
                  providerDetails?.employmentType === "Full-Time"
                    ? "Full-Time"
                    : "Per-Diem"
                }
              >
                {providerDetails?.employmentType === "Full-Time" ? "FT" : "PD"}
              </div>
            )}
        </div>
        <div className="text-sm text-gray-500">
          {providerDetails?.speciality} • {providerDetails?.patientCount}{" "}
          Patients
        </div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div
          className={`inline-flex items-center px-2 py-1 rounded ${getScoreColor(
            provider.score
          )}`}
        >
          {provider.score >= 90 ? (
            <Star className="mr-1 w-4 h-4" />
          ) : (
            <TrendingUp className="mr-1 w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {provider.score?.toFixed(0)}
          </span>
        </div>
      </td>
      {view !== "providers" && (
        <td className="px-6 py-4 text-center whitespace-nowrap">
          <div className="text-sm font-medium">
            {moment(provider?.date).format("MM/DD/YYYY") ?? "NA"}
          </div>
          {provider?.date && (
            <div className="text-sm font-medium">
              {moment(provider?.date).format("dddd")}
            </div>
          )}
        </td>
      )}
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div className="text-sm">{provider.proposedTimeslot}</div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div className="text-sm">
          {provider.proposedPosition} of {provider.visitCount + 1}
        </div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div className="text-sm">{provider.visitCount}</div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div className="text-sm">
          {Math.trunc(provider.originalDistance?.toFixed(0) ?? 0)} mi
        </div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div
          className={`inline-flex items-center px-2 py-1 text-sm rounded ${getDistanceColor(added)}`}
        >
          {added < 0
            ? `${Math.trunc(Math.round(Math.abs(added)))} ${Math.abs(Math.round(added)) === 1 ? "mile" : "miles"
            } saved`
            : `${Math.trunc(added)} ${Math.trunc(added) === 1 ? "mile" : "miles"
            } added`}
        </div>
      </td>
      <td className="px-6 py-4 text-center whitespace-nowrap">
        <div
          className={`inline-flex items-center px-2 py-1 text-sm rounded ${getDistanceColor(driveTime)}`}
        >
          {driveTime < 0
            ? (() => {
              const absTime = Math.abs(Math.round(driveTime));
              if (absTime < 60) {
                return `${absTime} ${absTime === 1 ? "minute" : "minutes"
                  } saved`;
              }
              const hours = Math.floor(absTime / 60);
              const minutes = absTime % 60;
              return `${hours} ${hours === 1 ? "hour" : "hours"}${minutes > 0
                ? ` ${minutes} ${minutes === 1 ? "minute" : "minutes"}`
                : ""
                } saved`;
            })()
            : (() => {
              const roundedTime = Math.round(driveTime);
              if (roundedTime < 60) {
                return `${roundedTime} ${roundedTime === 1 ? "minute" : "minutes"
                  } added`;
              }
              const hours = Math.floor(roundedTime / 60);
              const minutes = roundedTime % 60;
              return `${hours} ${hours === 1 ? "hour" : "hours"}${minutes > 0
                ? ` ${minutes} ${minutes === 1 ? "minute" : "minutes"}`
                : ""
                } added`;
            })()}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex justify-center space-x-2">
          <Tooltip title="View Map">
            <button
              onClick={handleMapClick}
              className="p-2 text-orange-600 bg-orange-50 rounded-md hover:text-orange-900"
              disabled={
                !user?.permissions?.includes("dispatch.view_map_dispatch")
              }
            >
              <Map className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip title="View Schedule">
            <button
              onClick={handleClick}
              className="p-2 text-blue-600 bg-blue-50 rounded-md hover:text-blue-900"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip title="Publish">
            <button
              onClick={handlePublishClick}
              className="p-2 text-green-600 bg-green-50 rounded-md hover:text-green-900"
              disabled={
                !user?.permissions?.includes("dispatch.publish_to_ehr_dispatch")
              }
            >
              <CloudUpload className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

// Same cumulative bucketing the backend's possibleInsertionsPerGroup uses
// (each threshold counts everything under it, not an exclusive range), but
// computed from the ETA-enriched distance instead of /dispatch's cheap
// pre-enrichment estimate - so the summary line above the table agrees with
// what meetsRoutingCriteria actually keeps visible in the rows below it.
const computeDistanceBuckets = (items) => {
  let under50 = 0;
  let under75 = 0;
  let under100 = 0;
  let above100 = 0;
  items.forEach((item) => {
    const added = item.proposedDistance - item.originalDistance;
    if (added < 50) under50 += 1;
    if (added < 75) under75 += 1;
    if (added < 100) under100 += 1;
    if (added >= 100) above100 += 1;
  });
  return { under50, under75, under100, above100 };
};

const TableComponent = ({
  providerAppointments: allProviderAppointments,
  setOpenProviderAppointments,
  fetchAppointmentsFunction,
  setProviderAppointment,
  view,
  providersData,
  setOpenMap,
  setOpenConfirmModal,
  setIsPublished,
  appointmentType,
  selectedPatient,
  user,
  routingCriteria,
  searchConsecutiveSlots,
  onDistanceBucketsUpdated,
  dispatchErrors,
  dispatchTiming,
  onDispatchEtaStarted,
  onDispatchEtaCompleted,
  onDispatchRenderReady,
}) => {
  // Apply the Routing Criteria bucket BEFORE paginating. The backend returns
  // every slot (sorted by score) and no longer filters by distance, so
  // paginating first and hiding rows per page left e.g. the ">= 100 miles"
  // bucket empty on page 1 while its matches sat on later pages. Distances here
  // are the pre-ETA estimate; rows are still re-checked against the real
  // ETA-enriched distance when rendered (rowMeetsRoutingCriteria in TableRow).
  const providerAppointments = useMemo(
    () =>
      (allProviderAppointments ?? []).filter((item) =>
        rowMeetsRoutingCriteria(item, routingCriteria)
      ),
    [allProviderAppointments, routingCriteria]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [currentItems, setCurrentItems] = useState([]);
  const itemsPerPage = 30;
  const prevProviderAppointmentsRef = useRef(providerAppointments);
  const isResettingPageRef = useRef(false);
  const etaRequestIdRef = useRef(0);
  const lastEtaPayloadRef = useRef(null);
  const initialPaintRef = useRef(null);
  const pagePaintRef = useRef(null);

  const buildEtaPayloadKey = (items) =>
    JSON.stringify({
      patientId: selectedPatient?.value,
      appointmentTypeId: appointmentType?.value,
      isConsecutiveSlots: searchConsecutiveSlots,
      data: items.map((item) => ({
        providerId: item.providerId,
        visitDate: item.date,
        timeslot: item.proposedTimeslot,
        sequenceNumber: item.proposedPosition,
        marketId: item.marketId,
      })),
    });

  // Calculate pagination
  const totalPages = Math.ceil(providerAppointments?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const [fetchProviderDispatchDataGoogleETA] =
    useFetchProviderDispatchDataGoogleETAMutation();

  const fetchGoogleETA = async (items, requestId) => {
    setIsLoading(true);
    const etaStartedAt = performance.now();
    const timingId = dispatchTiming?.timingId;
    const tracksInitialDispatch =
      currentPage === 1 &&
      timingId != null &&
      onDispatchEtaStarted?.(timingId, {
        requestedCount: items?.length ?? 0,
      }) === true;

    try {
      if (!items?.length) return;

      const data = items.map((item) => ({
        providerId: item.providerId,
        visitDate: item.date,
        timeslot: item.proposedTimeslot,
        sequenceNumber: item.proposedPosition,
        // The dispatch endpoint can offer a slot out of a non-primary market
        // location for multi-market providers. Without forwarding this, the
        // google-eta endpoint falls back to the provider's primary location
        // and can return a wildly wrong distance/drive-time for the slot.
        marketId: item.marketId,
      }));

      const googleETAData = await fetchProviderDispatchDataGoogleETA({
        requestId: dispatchTiming?.requestId,
        dispatchId: dispatchTiming?.dispatchId,
        patientId: selectedPatient?.value,
        appointmentTypeId: appointmentType?.value,
        isConsecutiveSlots: searchConsecutiveSlots,
        data,
      }).unwrap();

      if (requestId !== etaRequestIdRef.current) return;
      const etaResults = Array.isArray(googleETAData?.data)
        ? googleETAData.data
        : [];

      const newCurrentItems = items.flatMap((item) => {
        const eta = etaResults.find(
          (eta) =>
            eta.providerId === item.providerId &&
            eta.visitDate === item.date &&
            eta.proposedSequenceNumber === item.proposedPosition
             &&
            moment(eta.timeslot, ["h:mm A", "hh:mm A"]).format("HH:mm") ===
              moment(item.proposedTimeslot, ["h:mm A", "hh:mm A"]).format(
                "HH:mm"
              )
        );

        // NOTE: a first-of-day slot's eta.proposedDistance from
        // /dispatch/google-eta is a ONE-WAY provider->patient distance,
        // matching the raw /dispatch estimate (e.g. 195 mi eta vs 193.63 mi
        // raw, ~3hr duration consistent with a one-way drive) - not a round
        // trip. Do not halve it; an earlier "round-trip correction" here was
        // based on a wrong assumption and silently understated real
        // distance, pushing genuinely-far slots into the wrong Routing
        // Criteria bucket.

        // The live route says the corrected slot can't be booked (not enough
        // travel time before the next visit, or past the provider's
        // availability) - don't offer it.
        if (eta?.travelConflict === true || eta?.outsideAvailability === true) {
          return [];
        }

        return [
          {
            ...item,
            proposedTimeslot: eta?.updatedTimeslot ?? item.proposedTimeslot,
            originalDistance: eta?.originalDistance ?? item.originalDistance,
            proposedDistance: eta?.proposedDistance ?? item.proposedDistance,
            originalDuration: eta?.originalDuration ?? item.originalDuration,
            proposedDuration: eta?.proposedDuration ?? item.proposedDuration,
            score: eta?.score ?? item.score,
          },
        ];
      });

      const failedCount = Math.max(0, items.length - etaResults.length);
      if (tracksInitialDispatch) {
        onDispatchEtaCompleted?.(timingId, {
          status: failedCount > 0 ? "partial" : "success",
          returnedCount: etaResults.length,
          failedCount,
        });
        initialPaintRef.current = {
          timingId,
          renderedCount: newCurrentItems.length,
        };
      } else {
        pagePaintRef.current = {
          startedAt: etaStartedAt,
          etaCompletedAt: performance.now(),
          page: currentPage,
          requestedCount: items.length,
          returnedCount: etaResults.length,
          failedCount,
          renderedCount: newCurrentItems.length,
          status: failedCount > 0 ? "partial" : "success",
        };
      }
      setCurrentItems(newCurrentItems.sort((a, b) => b.score - a.score));
      // Only trust this as the summary total when everything fits on one
      // page - newCurrentItems is just the current page's slice, and
      // overwriting the summary with a partial page's counts on a
      // multi-page result set would replace a global (if pre-enrichment)
      // total with a misleadingly small one.
      // providerAppointments is already narrowed to the selected bucket, so
      // also require that nothing was filtered out - otherwise this slice is
      // only the matching subset and would overwrite the global bucket counts
      // (e.g. "< 50: 0 | ≥ 100: 22" when viewing the ≥ 100 bucket).
      if (
        totalPages <= 1 &&
        newCurrentItems.length === (allProviderAppointments?.length ?? 0)
      ) {
        onDistanceBucketsUpdated?.(computeDistanceBuckets(newCurrentItems));
      }
    } catch {
      if (requestId !== etaRequestIdRef.current) return;

      if (tracksInitialDispatch) {
        onDispatchEtaCompleted?.(timingId, {
          status: "error",
          returnedCount: 0,
          failedCount: items.length,
        });
        initialPaintRef.current = {
          timingId,
          renderedCount: items.length,
        };
      } else {
        pagePaintRef.current = {
          startedAt: etaStartedAt,
          etaCompletedAt: performance.now(),
          page: currentPage,
          requestedCount: items.length,
          returnedCount: 0,
          failedCount: items.length,
          renderedCount: items.length,
          status: "error",
        };
      }
      setCurrentItems(items);
    } finally {
      if (requestId === etaRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const appointmentsChanged =
      prevProviderAppointmentsRef.current !== providerAppointments;
    prevProviderAppointmentsRef.current = providerAppointments;

    if (appointmentsChanged) {
      lastEtaPayloadRef.current = null;
      if (currentPage !== 1) {
        isResettingPageRef.current = true;
        setCurrentPage(1);
        return;
      }
    } else if (isResettingPageRef.current && currentPage !== 1) {
      return;
    }

    isResettingPageRef.current = false;

    const newItems = providerAppointments?.slice(startIndex, endIndex);
    if (!newItems?.length) {
      setCurrentItems(newItems);
      lastEtaPayloadRef.current = null;
      return;
    }

    const payloadKey = buildEtaPayloadKey(newItems);
    if (payloadKey === lastEtaPayloadRef.current) return;
    lastEtaPayloadRef.current = payloadKey;

    const requestId = ++etaRequestIdRef.current;
    fetchGoogleETA(newItems, requestId);
  }, [currentPage, providerAppointments]);

  useEffect(() => {
    const initialPaint = initialPaintRef.current;
    if (initialPaint !== null) {
      initialPaintRef.current = null;
      onDispatchRenderReady?.(
        initialPaint.timingId,
        initialPaint.renderedCount
      );
    }

    const pagePaint = pagePaintRef.current;
    if (pagePaint === null) return undefined;
    pagePaintRef.current = null;

    let secondFrameId = null;
    const firstFrameId = requestAnimationFrame(() => {
      secondFrameId = requestAnimationFrame(() => {
        const paintedAt = performance.now();
        pushFaroMeasurement(
          "dispatch_eta_page_displayed",
          {
            eta_api_duration_ms: Math.max(
              0,
              Math.round(pagePaint.etaCompletedAt - pagePaint.startedAt)
            ),
            render_duration_ms: Math.max(
              0,
              Math.round(paintedAt - pagePaint.etaCompletedAt)
            ),
            total_duration_ms: Math.max(
              0,
              Math.round(paintedAt - pagePaint.startedAt)
            ),
            eta_requested_count: pagePaint.requestedCount,
            eta_returned_count: pagePaint.returnedCount,
            eta_failed_count: pagePaint.failedCount,
            rendered_result_count: pagePaint.renderedCount,
          },
          {
            request_id: String(dispatchTiming?.requestId ?? "unknown"),
            dispatch_id: String(dispatchTiming?.dispatchId ?? "unknown"),
            page: String(pagePaint.page),
            status: pagePaint.status,
          }
        );
      });
    });

    return () => {
      cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== null) cancelAnimationFrame(secondFrameId);
    };
  }, [
    currentItems,
    dispatchTiming?.dispatchId,
    dispatchTiming?.requestId,
    onDispatchRenderReady,
  ]);

  const renderPaginationButtons = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      // If total pages is less than max visible, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        endPage = 4;
      }
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // currentItems is the raw, ETA-enriched page slice - individual rows can
  // still fail rowMeetsRoutingCriteria (their real distance doesn't match
  // the selected bucket) and hide themselves. Filtering here too means the
  // empty-state check below reflects what's actually visible, instead of
  // showing pagination/a populated-looking table around a tbody where every
  // row silently rendered null.
  const visibleItems = (currentItems ?? []).filter((item) =>
    rowMeetsRoutingCriteria(item, routingCriteria)
  );
  // Either nothing in the whole result set falls in the selected bucket, or
  // this page's rows all failed the real (ETA-enriched) distance re-check.
  const foundCount = allProviderAppointments?.length ?? 0;
  const hiddenByRoutingCriteria =
    (foundCount > 0 && providerAppointments.length === 0) ||
    ((currentItems?.length ?? 0) > 0 && visibleItems.length === 0);

  return (
    <div
      className={classNames(
        "flex overflow-hidden flex-col bg-white rounded-lg border border-gray-200 shadow-sm",
        visibleItems.length === 0 ? " h-fit" : " h-full"
      )}
    >
      <div className="overflow-x-auto relative flex-1">
        {isLoading && (
          <div className="flex fixed inset-0 z-50 justify-center items-center bg-white bg-opacity-75">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              <p className="mt-2 text-sm text-gray-600">Loading...</p>
            </div>
          </div>
        )}
        {hiddenByRoutingCriteria ? (
          <div className="flex flex-col gap-2 justify-center items-center px-6 py-16 text-center">
            <p className="text-base font-semibold text-gray-700">
              No providers match the selected Routing Criteria
            </p>
            <p className="text-sm text-gray-500">
              {foundCount} provider{foundCount === 1 ? "" : "s"} found for this search, but none fall within the selected distance range. Try a different Routing Criteria.
            </p>
            <DispatchErrorsList dispatchErrors={dispatchErrors} />
          </div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 z-10 text-white bg-blue-600">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Rank
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-left whitespace-nowrap"
              >
                Provider
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Match
              </th>
              {view !== "providers" && (
                <th
                  scope="col"
                  className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
                >
                  Date
                </th>
              )}
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Time
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Sequence
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Visits
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Original distance
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Added distance
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Drive time
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-sm font-medium text-center whitespace-nowrap"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visibleItems.length > 0 ? (
              visibleItems.map((provider, index) => (
                <TableRow
                  key={`${provider.providerId}-${provider.date}-${provider.proposedPosition}-${provider.proposedTimeslot}`}
                  provider={provider}
                  providersData={providersData}
                  fetchAppointmentsFunction={fetchAppointmentsFunction}
                  setProviderAppointment={setProviderAppointment}
                  setOpenProviderAppointments={setOpenProviderAppointments}
                  view={view}
                  setOpenMap={setOpenMap}
                  rank={startIndex + index + 1}
                  setOpenConfirmModal={setOpenConfirmModal}
                  setIsPublished={setIsPublished}
                  user={user}
                  routingCriteria={routingCriteria}
                />
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center">
                  <span className="whitespace-nowrap">No providers found</span>
                  <DispatchErrorsList dispatchErrors={dispatchErrors} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {/* Pagination */}
      {visibleItems.length > 0 && (
        <div className="flex sticky bottom-0 justify-between items-center px-6 py-3 bg-white border-t border-gray-200 shadow-lg">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="inline-flex relative items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="inline-flex relative items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(endIndex, providerAppointments?.length)}
                </span>{" "}
                of{" "}
                <span className="font-medium">
                  {providerAppointments?.length}
                </span>{" "}
                results
              </p>
            </div>
            <div>
              <nav
                className="inline-flex relative z-0 -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="inline-flex relative items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white rounded-l-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {renderPaginationButtons().map((pageNum, index) => (
                  <button
                    key={index}
                    className={classNames(
                      "relative inline-flex items-center px-4 py-2 text-sm font-medium border border-gray-300",
                      pageNum === "..."
                        ? "bg-white text-gray-700 cursor-default"
                        : currentPage === pageNum
                          ? "bg-blue-500 text-white hover:bg-blue-400 ease-in-out duration-150"
                          : "bg-white text-gray-700 hover:bg-gray-900 ease-in-out duration-150"
                    )}
                    onClick={() => pageNum !== "..." && setCurrentPage(pageNum)}
                    disabled={pageNum === "..." || isLoading}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className="inline-flex relative items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white rounded-r-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
